import * as React from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { getColor } from '../theme/colors'

const plusIcon = (
  <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
    <rect width="32" height="32" rx="16" fill="#F4F4F4" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.5 9.5a1.5 1.5 0 00-3 0v5h-5a1.5 1.5 0 000 3h5v5a1.5 1.5 0 003 0v-5h5a1.5 1.5 0 000-3h-5v-5z"
      stroke="none"
    />
  </svg>
)

const minusIcon = (
  <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
    <rect width="32" height="32" rx="16" fill="#F4F4F4" />
    <path d="M21.5 16h-11" stroke="none" strokeWidth="3" strokeMiterlimit="10" strokeLinecap="round" />
  </svg>
)
export type StepperLabelProps = {
  invalid?: boolean
  plusDisabled?: boolean
  minusDisabled?: boolean
}
export type StepperInputProps = {
  value: number
  onChange?: (value: number) => void
  label?: string
  className?: string // TODO: Shouldn't need this
  disabled?: boolean
  invalid?: boolean
  min?: number
  max?: number
}

const WrappingLabel = styled.label<StepperLabelProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  user-select: none;

  span:first-of-type {
    flex-grow: 1;
    ${props => props.theme.typography.inputText}
  }

  input {
    border: none;
    cursor: default;
    width: 0;
    flex-basis: 3rem;
    font-family: inherit;
    text-align: center;
    font-size: 1.5rem;
    background: transparent;
    color: ${getColor('primary')};
    font-weight: ${props => props.theme.weights.medium};
    line-height: 2rem;
    user-select: none;
    -webkit-touch-callout: none;

    ::selection {
      background: transparent;
    }
  }

  button {
    cursor: pointer;
    width: 2rem;
    height: 2rem;
    background: transparent;
    border: none;
    outline: none;
    box-sizing: border-box;
    display: block;
    padding: 0;
  }

  input,
  button:focus {
    outline: none;
    outline-style: none;
  }

  ${props => props.theme.selectors.keyboard} & button:focus {
    outline-style: auto;
  }

  color: ${getColor('label')};

  rect {
    fill: ${getColor('secondaryButtonBackground')};
  }

  button:first-of-type path {
    stroke: ${getColor('secondaryButtonTint')};
  }

  button:last-of-type path {
    fill: ${getColor('secondaryButtonTint')};
  }
  ${({ invalid, theme }) => {
    return (
      invalid &&
      css`
        border-radius: 1rem;
        border: 1px solid ${theme.colors.error};
      `
    )
  }}
  ${({ plusDisabled, theme }) => {
    return (
      plusDisabled &&
      css`
        button:last-of-type path {
          fill: ${theme.colors.disabledLabel};
          stroke: ${theme.colors.disabledLabel};
        }
      `
    )
  }}
    ${({ minusDisabled, theme }) => {
    return (
      minusDisabled &&
      css`
        button:first-of-type path {
          stroke: ${theme.colors.disabledLabel};
        }
      `
    )
  }}
`

const StepperInput = React.forwardRef(
  (
    { value, disabled, min, max, invalid, className, label, onChange }: StepperInputProps,
    ref: React.Ref<HTMLLabelElement>
  ) => {
    const minusDisabled = disabled || value <= 0 || (min != null && value <= min)
    const plusDisabled = disabled || value >= 99 || (max != null && value >= max)

    const handleIncrement = () => {
      if (!plusDisabled) {
        onChange?.(value + 1)
      }
    }
    const handleDecrement = () => {
      if (!minusDisabled) {
        onChange?.(value - 1)
      }
    }
    return (
      <WrappingLabel
        className={className}
        minusDisabled={minusDisabled}
        plusDisabled={plusDisabled}
        invalid={invalid}
        onClick={e => e.preventDefault()}
        ref={ref}
      >
        <span>{label}</span>
        <button aria-label={`Minus one from ${label}`} onClick={handleDecrement}>
          {minusIcon}
        </button>
        <input aria-label={`${value} ${label}`} type="input" value={value} readOnly></input>
        <button aria-label={`Add one to ${label}`} onClick={handleIncrement}>
          {plusIcon}
        </button>
      </WrappingLabel>
    )
  }
)

export default StepperInput
